import React from 'react';
import styled from 'styled-components';
import { Crown, Search, X, Users, ShieldCheck } from 'lucide-react';
import { CompanyUserDto } from '@/services/companyUserService';
import { fadeIn, fadeInUp, LoadingSpinner } from '../../SharedStyles';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import Avatar from '@/common/components/avatar/Avatar';

interface AddMasterModalProps {
  showAddMasterPanel: boolean;
  setShowAddMasterPanel: (show: boolean) => void;
  nonMasterUsers: CompanyUserDto[];
  filteredNonMasterUsers: CompanyUserDto[];
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  handleSetMaster: (companyUserId: number) => void;
  masterActionLoading: number | null;
}

const AddMasterModal: React.FC<AddMasterModalProps> = ({
  showAddMasterPanel,
  setShowAddMasterPanel,
  nonMasterUsers,
  filteredNonMasterUsers,
  searchQuery,
  setSearchQuery,
  handleSetMaster,
  masterActionLoading
}) => {
  if (!showAddMasterPanel) return null;

  const handleClose = () => {
    setShowAddMasterPanel(false);
    setSearchQuery('');
  };

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalIconWrapper>
            <Crown size={24} />
          </ModalIconWrapper>
          <ModalTitleArea>
            <ModalTitle>마스터 추가</ModalTitle>
            <ModalSubtitle>팀원 중에서 마스터로 지정할 사람을 선택하세요</ModalSubtitle>
          </ModalTitleArea>
          <ModalCloseButton onClick={handleClose}>
            ✕
          </ModalCloseButton>
        </ModalHeader>

        {/* 검색 입력 */}
        {nonMasterUsers.length > 0 && (
          <SearchInputWrapper>
            <SearchIcon>
              <Search size={18} />
            </SearchIcon>
            <SearchInput
              type="text"
              placeholder="이름, 이메일, 직책으로 검색..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
            {searchQuery && (
              <SearchClearButton onClick={() => setSearchQuery('')}>
                <X size={16} />
              </SearchClearButton>
            )}
          </SearchInputWrapper>
        )}
        
        <ModalContent>
          {nonMasterUsers.length > 0 ? (
            filteredNonMasterUsers.length > 0 ? (
              <MemberSelectList>
                {filteredNonMasterUsers.map((member) => (
                  <MemberSelectItem key={member.id}>
                    <MemberSelectInfo>
                      <Avatar alt={member.name || '사용자'} isUser={true} sizes="md" />
                      <MemberSelectDetails>
                        <MemberSelectName>{member.name || '이름 없음'}</MemberSelectName>
                        <MemberSelectEmail>{member.email || '이메일 없음'}</MemberSelectEmail>
                        {member.position && (
                          <MemberSelectPosition>{member.position}</MemberSelectPosition>
                        )}
                      </MemberSelectDetails>
                    </MemberSelectInfo>
                    <MasterActionButton
                      $variant="add"
                      onClick={() => handleSetMaster(member.id)}
                      disabled={masterActionLoading === member.id}
                    >
                      {masterActionLoading === member.id ? (
                        <LoadingSpinner />
                      ) : (
                        <>
                          <ShieldCheck size={16} />
                          <span>마스터 지정</span>
                        </>
                      )}
                    </MasterActionButton>
                  </MemberSelectItem>
                ))}
              </MemberSelectList>
            ) : (
              <NoSearchResultMessage>
                <Search size={32} />
                <span>"{searchQuery}" 검색 결과가 없습니다</span>
              </NoSearchResultMessage>
            )
          ) : (
            <NoMembersMessage>
              <NoMembersIcon>
                <Users size={48} />
              </NoMembersIcon>
              <NoMembersTitle>추가할 팀원이 없습니다</NoMembersTitle>
              <NoMembersDesc>
                모든 팀원이 이미 마스터이거나,<br />
                아직 팀원이 없습니다.
              </NoMembersDesc>
            </NoMembersMessage>
          )}
        </ModalContent>

        <ModalFooter>
          <ModalFooterText>
            <Users size={14} />
            <span>
              {searchQuery 
                ? `${filteredNonMasterUsers.length}명 검색됨 (전체 ${nonMasterUsers.length}명)`
                : `총 ${nonMasterUsers.length}명의 팀원`
              }
            </span>
          </ModalFooterText>
          <ModalCloseAction onClick={handleClose}>
            닫기
          </ModalCloseAction>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

// 모달 스타일
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.2s ease-out;
  padding: 1rem;
`;

const ModalContainer = styled.div`
  width: 100%;
  max-width: 520px;
  max-height: 85vh;
  background: ${semanticColors.background.primary};
  border-radius: 24px;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  animation: ${fadeInUp} 0.3s ease-out;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.5rem 1.5rem 1.25rem;
  border-bottom: 1px solid ${semanticColors.border.primary};
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, transparent 100%);
`;

const ModalIconWrapper = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border-radius: 14px;
  color: #fff;
  flex-shrink: 0;
`;

const ModalTitleArea = styled.div`
  flex: 1;
`;

const ModalTitle = styled.h2`
  ${typography.heading.h5}
  color: ${semanticColors.text.primary};
  margin-bottom: 0.25rem;
`;

const ModalSubtitle = styled.p`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
`;

const ModalCloseButton = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.tertiary};
  border: none;
  border-radius: 10px;
  color: ${semanticColors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  font-size: 1.25rem;
  
  &:hover {
    background: ${semanticColors.background.danger.secondary};
    color: ${semanticColors.text.onDanger.primary};
  }
`;

// 검색 입력
const SearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  padding: 0 1.5rem;
  margin-bottom: 0.5rem;
  margin-top: 1rem;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 2.25rem;
  color: ${semanticColors.text.tertiary};
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.875rem 1rem 0.875rem 2.75rem;
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 12px;
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.primary};
  transition: all 0.2s ease;
  
  &::placeholder {
    color: ${semanticColors.text.disabled};
  }
  
  &:focus {
    outline: none;
    border-color: ${semanticColors.border.brand};
    background: ${semanticColors.background.primary};
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
  }
`;

const SearchClearButton = styled.button`
  position: absolute;
  right: 2rem;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.tertiary};
  border: none;
  border-radius: 6px;
  color: ${semanticColors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${semanticColors.background.danger.secondary};
    color: ${semanticColors.text.onDanger.primary};
  }
`;

const NoSearchResultMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 3rem 2rem;
  color: ${semanticColors.text.tertiary};
  ${typography.paragraph.p2.default}
  text-align: center;
  
  svg {
    opacity: 0.4;
  }
`;

const ModalContent = styled.div`
  flex: 1;
  padding: 1.25rem 1.5rem;
  overflow-y: auto;
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-top: 1px solid ${semanticColors.border.primary};
  background: ${semanticColors.background.secondary};
`;

const ModalFooterText = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
`;

const ModalCloseAction = styled.button`
  padding: 0.625rem 1.25rem;
  background: ${semanticColors.background.tertiary};
  border: none;
  border-radius: 8px;
  ${typography.paragraph.p2.default}
  font-weight: 500;
  color: ${semanticColors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${semanticColors.background.primary};
    color: ${semanticColors.text.primary};
  }
`;

const MemberSelectList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const MemberSelectItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 14px;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${semanticColors.border.brand};
    background: ${semanticColors.background.tertiary};
    transform: translateX(4px);
  }
`;

const MemberSelectInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  flex: 1;
  min-width: 0;
`;

const MemberSelectDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const MemberSelectName = styled.p`
  ${typography.paragraph.p2.default}
  font-weight: 600;
  color: ${semanticColors.text.primary};
  margin-bottom: 0.125rem;
`;

const MemberSelectEmail = styled.p`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MemberSelectPosition = styled.span`
  display: inline-block;
  margin-top: 0.375rem;
  padding: 0.125rem 0.5rem;
  background: ${semanticColors.background.brand};
  border-radius: 4px;
  ${typography.caption.caption2}
  color: #fff;
`;

const NoMembersMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3rem 2rem;
  text-align: center;
`;

const NoMembersIcon = styled.div`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.tertiary};
  border-radius: 50%;
  color: ${semanticColors.text.disabled};
  margin-bottom: 1.25rem;
`;

const NoMembersTitle = styled.h3`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
  margin-bottom: 0.5rem;
`;

const NoMembersDesc = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.tertiary};
  line-height: 1.6;
`;

const MasterActionButton = styled.button<{ $variant: 'add' | 'remove' }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  border: none;
  border-radius: 8px;
  ${typography.caption.caption1.medium}
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.$variant === 'add' && `
    background: ${semanticColors.background.tertiary};
    color: ${semanticColors.text.brand};
  
    &:hover:not(:disabled) {
      background: ${semanticColors.background.brand};
      color: #fff;
    }
  `}
  
  ${props => props.$variant === 'remove' && `
    background: ${semanticColors.background.danger.secondary};
    color: ${semanticColors.text.onDanger.secondary};
    
    &:hover:not(:disabled) {
      background: ${semanticColors.background.danger.primary};
      color: #fff;
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export default AddMasterModal;
